import {StageProfile, inAdminOrder, stageLabel} from './projectStageUtil';
import {visibleStages} from './opportunityStages';

type StageIntl = Parameters<typeof stageLabel>[1];

/**
 * The input fields that move a project to the first stage with the given profile: the profile as `status`. The
 * server lands it in the company's first live stage with that profile, so a stage id cached at app start that has
 * since been deleted can't make the save fail.
 */
export const stageChangeInput = (profile: StageProfile): {status: StageProfile} => ({status: profile});

export type StagePickerOption = {value: string; label: string; profile: StageProfile | string | null};

/** A company stage as the pickers receive it, from the stage cache or from a Relay fragment. */
type PickerStage = {id: string; name: string | null; sortOrder: number | null; profile: StageProfile | string | null};

/**
 * The stage picker's options, `value` being the stage id: every company stage in admin order. Opportunity stages are
 * left out unless `showOpportunity` is set or the project is already in that stage.
 */
export const stagePickerOptions = (
	stages: ReadonlyArray<PickerStage>,
	opts: {showOpportunity: boolean; currentStageId: string | null},
	intl: StageIntl
): StagePickerOption[] => {
	const visible = visibleStages(inAdminOrder(stages), opts.currentStageId ? [opts.currentStageId] : [], opts.showOpportunity);
	return visible.map(stage => ({value: stage.id, label: stageLabel(stage, intl), profile: stage.profile}));
};

/**
 * The option showing the project's current stage: the option with its id, else (only for a stage missing from the list)
 * the option with its profile.
 */
export const selectedStageOption = <T extends {value: string; profile: StageProfile | string | null}>(
	options: T[],
	stage: {id?: string | null; profile?: StageProfile | string | null} | null | undefined
): T | undefined => {
	if (!stage) {
		return undefined;
	}
	return (
		options.find(option => option.value === stage.id) ??
		(stage.profile ? options.find(option => option.profile === stage.profile) : undefined)
	);
};
